import ExceededConversionError from "@/errors/ExceededConversionError";
import ValidationError from "@/errors/ValidationError";
import { getFileExtension } from "@/utils/files";
import { formatBytes } from "@/utils/miscellaneous";
import { hasFreeOperations } from "@/utils/accessControl";
import { getI18nInstance } from "@/utils/i18n";

/**
 * Maximum file size allowed (in bytes).
 */
export const MAX_FILE_SIZE_BYTES = 10485760; // 10MB
/**
 * Sets maximum free conversion number for a user per day.
 * Allowed values:
 *  - null or negative integer number: unlimited conversions
 *  - positive integer number: specific conversions limit
 */
export const MAX_FREE_CONVERSION_PER_DAY = 5;

/**
 * All supported file extensions array.
 * The '*' wildcard in SUPPORTED_CONVERSIONS array will be replaced with it.
 */
export const ALLOWED_FILE_EXTENSIONS = [
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
  "ppt",
  "pptx",
  "pps", // document, excel, presentation
  "jpg",
  "jpeg",
  "png",
  "gif",
  "bmp",
  "tiff",
  "webp",
  "svg", // all kind of images
] as const;

/**
 * Supported conversions in 2-level groups.
 * First level is the category (universal, document and etc.).
 * The second level is mapped allowed source extension (the key) to target format/s (the value).
 *
 * Syntax for source extension key:
 *  - single value string: ex. "pdf" or "doc";
 *  - multiple values string with "," delimiter: ex. "doc, docx" or "jpg, png, gif";
 *  - wildcard '*' to replace with all file extensions (see above ALLOWED_FILE_EXTENSIONS array): ex. "*".
 *
 * Syntax for target format/s:
 *  - only one target format is specified as string: ex. "pdf" or "jpg";
 *  - multiple target formats is specified as array of strings: ex. ["docx", "jpg", "pptx"].
 */
export const SUPPORTED_CONVERSIONS = {
  universal: {
    pdf: ["docx", "jpg", "pptx"],
    "*": "pdf",
  },
  document: {
    pdf: "docx",
    "doc, docx": "pdf",
  },
  image: {
    pdf: "jpg",
    "jpg, jpeg, png, gif, bmp, tiff, webp, svg": "pdf",
  },
};

export type SupportedConversionsType = typeof SUPPORTED_CONVERSIONS;
export type SupportedConversionsCategories = keyof SupportedConversionsType;

export function validateInputFile(
  file: File,
  category: SupportedConversionsCategories
): void {
  const i18n = getI18nInstance();

  if (!hasFreeOperations("conversion")) {
    throw new ExceededConversionError(
      i18n.translate("pdfextra_convert_tool.errors.max_free_conversions", {
        MAX_FREE_CONVERSION_PER_DAY: Number(
          MAX_FREE_CONVERSION_PER_DAY
        ).toString(),
      })
    );
  }

  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new ValidationError(
      i18n.translate("pdfextra_convert_tool.errors.file_too_large", {
        MAX_FILE_SIZE: formatBytes(MAX_FILE_SIZE_BYTES),
      })
    );
  }

  const allowedExtensions = getAllowedExtensions(category);
  const extension = getFileExtension(file.type);

  if (!allowedExtensions.includes(extension)) {
    throw new ValidationError(
      i18n.translate("pdfextra_convert_tool.errors.not_supported_file_format", {
        allowedExtensions: allowedExtensions.join(", "),
      })
    );
  }
}

export function getTargetFormats<
  Category extends SupportedConversionsCategories,
  Extension extends keyof SupportedConversionsType[Category]
>(
  category: Category,
  extension: Extension
): SupportedConversionsType[Category][Extension] | string[] {
  const categoryFormats = SUPPORTED_CONVERSIONS[category];

  if (typeof categoryFormats[extension] !== "undefined") {
    return categoryFormats[extension];
  }

  if (typeof categoryFormats["*" as Extension] !== "undefined") {
    return categoryFormats["*" as Extension];
  }

  for (const key in categoryFormats) {
    if (key.includes(extension as string)) {
      return categoryFormats[key] as any;
    }
  }

  return [];
}

export function getAllowedExtensions(
  category: SupportedConversionsCategories
): string[] {
  const formats = Object.keys(SUPPORTED_CONVERSIONS[category])
    .map((element) => element.split(",").map((format) => format.trim()))
    .flat();
  const wildcardIndex = formats.indexOf("*");

  if (wildcardIndex > -1) {
    formats.splice(wildcardIndex, 1);
    formats.push(...ALLOWED_FILE_EXTENSIONS);
  }

  // @ts-ignore
  return [...new Set(formats)];
}
